// A read-only peek at one project, opened from the list and the board. The frame is ui/Peek;
// this is what it shows.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import Peek, { PeekFact, PeekSection } from '../components/ui/Peek';
import Progress from '../components/ui/Progress';
import Skeleton from '../components/ui/Skeleton';
import { getProduction } from './sparkApi';
import type { ProductionDetail } from './sparkApi';
import { SpiceMeter, StageTag, StatusTag, TaskStatusTag } from './SparkBits';
import { SPICE, firstName } from './sparkRules';
import type { ReviewStep } from './sparkRules';
import { dayText, errorMessage, statusOf } from './sparkUi';

const REVIEWER: Record<ReviewStep, string> = {
  designer: 'the designer',
  director: 'the director',
  producer: 'the producer',
  client: 'the client',
  pilot: 'the pilot',
};

const waitingLine = (w: NonNullable<ProductionDetail['production']['waiting_review']>): string => {
  if (w.step === 'pilot') return `Waiting on the pilot's answer: ${w.task}`;
  const who = w.name ? firstName(w.name) : REVIEWER[w.step];
  return `Waiting on ${who}'s review: ${w.task}`;
};

const roleLabel = (role: string) => role.charAt(0).toUpperCase() + role.slice(1);

const PeekSkeleton: React.FC = () => (
  <div aria-busy="true" aria-label="Loading project" className="space-y-5">
    <div className="flex gap-2"><Skeleton className="h-5 w-20 rounded-full" /><Skeleton className="h-5 w-28 rounded-full" /></div>
    <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
    <Skeleton className="h-20 w-full" />
    <Skeleton className="h-32 w-full" />
  </div>
);

const PeekBody: React.FC<{ detail: ProductionDetail }> = ({ detail }) => {
  const { production: p, events, team, tasks } = detail;
  const top = tasks.filter((t) => !t.parent_id && t.status !== 'closed').sort((a, b) => a.position - b.position);
  const spice = SPICE.find((s) => s.level === p.spice);
  const hasStatus = !!statusOf(p);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <StageTag stage={p.stage} />
        {hasStatus && <StatusTag row={p} />}
      </div>

      {p.waiting_review && (
        <p className="m-0 rounded-control bg-surface-container px-3 py-2 text-sm text-on-surface">{waitingLine(p.waiting_review)}</p>
      )}

      <dl className="m-0 space-y-1">
        <PeekFact label="Show date">{p.is_standing ? 'Ongoing' : p.show_date ? dayText(p.show_date, 'MMM D, YYYY') : 'No date'}</PeekFact>
        <PeekFact label="Preview due">{p.preview_due ? dayText(p.preview_due, 'MMM D, YYYY') : 'Not set'}</PeekFact>
        <PeekFact label="Spice">
          <span className="inline-flex items-center gap-2 whitespace-nowrap"><SpiceMeter level={p.spice} />{spice && <span>{spice.label}</span>}</span>
        </PeekFact>
      </dl>

      <PeekSection title="Deliverables">
        {top.length === 0 ? (
          <EmptyState variant="line">No deliverables yet.</EmptyState>
        ) : (
          <>
            <Progress
              value={p.ready}
              max={p.deliverables}
              label="Deliverables ready"
              display="count"
              celebrate={p.deliverables > 0 && p.ready === p.deliverables}
              className="mb-3"
            />
            <ul className="m-0 grid list-none gap-px overflow-hidden rounded-control p-0">
              {top.map((t) => (
                <li key={t.id} className="bg-surface-container px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 text-sm text-on-surface">{t.name}</span>
                    <span className="flex-none"><TaskStatusTag status={t.status} /></span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-on-surface-variant">
                    <span className="min-w-0 truncate">{t.assignee_name || 'Unassigned'}</span>
                    <span className="flex-none tabular-nums">{t.due_date ? dayText(t.due_date.slice(0, 10), 'MMM D') : 'No due date'}</span>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </PeekSection>

      <PeekSection title="Show team">
        {team.length === 0 ? (
          <EmptyState variant="line">No team drawn yet.</EmptyState>
        ) : (
          <ul className="m-0 list-none space-y-1 p-0">
              {team.map((m) => (
                <li key={m.role} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="flex-none text-on-surface-variant">{roleLabel(m.role)}</span>
                  <span className="min-w-0 truncate text-right text-on-surface">
                    {m.user_id ? m.name || 'Someone' : 'Not picked'}
                    {m.user_id && <span className="ml-2 text-xs text-on-surface-variant">{m.approved_at ? 'Approved' : 'Draft'}</span>}
                  </span>
                </li>
              ))}
            </ul>
        )}
      </PeekSection>

      <PeekSection title="Events">
        {events.length === 0 ? (
          <p className="m-0 text-sm text-on-surface-variant">Not linked to an event yet.</p>
        ) : (
          <ul className="m-0 list-none space-y-1 p-0">
            {events.map((e) => (
              <li key={e.id} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-on-surface">{e.name}</span>
                <span className="flex-none text-xs tabular-nums text-on-surface-variant">{dayText(e.event_date, 'MMM D')}</span>
              </li>
            ))}
          </ul>
        )}
      </PeekSection>
    </div>
  );
};

const SparkPeek: React.FC<{
  id: string | null;
  name: string;
  onClose: () => void;
  /** Where focus goes when the peek closes: the row or button that opened it. */
  returnTo: React.RefObject<HTMLElement | null>;
  /** Moves the peek to the previous or next project in the list's order. */
  onStep?: (dir: 1 | -1) => void;
  /** Where the shown project sits in that order, for the step buttons. */
  position?: { index: number; total: number };
}> = ({ id, name, onClose, returnTo, onStep, position }) => {
  const [detail, setDetail] = useState<ProductionDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const wasOpen = useRef(false);

  useEffect(() => {
    // A fresh opening starts from the skeleton; a step keeps the last project, dimmed, until the next arrives.
    if (id && !wasOpen.current) setDetail(null);
    wasOpen.current = !!id;
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let live = true;
    setError(null);
    getProduction(id)
      .then((d) => { if (live) setDetail(d); })
      .catch((e) => { if (live) setError(errorMessage(e, 'Could not load this project.')); });
    return () => { live = false; };
  }, [id, reloadKey]);

  const stale = !!detail && detail.production.id !== id;
  const title = (!stale && detail?.production.name) || name || 'Project';

  return (
    <Peek
      open={!!id}
      onClose={onClose}
      label={`Project preview: ${title}`}
      title={title}
      returnTo={returnTo}
      step={onStep && position ? { ...position, onStep, noun: 'project' } : undefined}
      contentKey={error ? 'error' : detail?.production.id ?? 'loading'}
      busy={stale && !error}
      footer={id ? <Link to={`/spark/projects/${id}`} className={`${buttonClass('filled', 'md')} w-full`}>Open project</Link> : null}
    >
      {error ? (
        <Alert tone="error" onRetry={() => setReloadKey((k) => k + 1)}>{`${error} Check your connection and try again.`}</Alert>
      ) : detail ? (
        <PeekBody detail={detail} />
      ) : (
        <PeekSkeleton />
      )}
    </Peek>
  );
};

export default SparkPeek;
